/**
 * FilesList Component
 *
 * List view for displaying file attachments from message history
 */

import React from 'react';
import type { SearchResult } from '../../services/search.service';

interface FilesListProps {
  messages: SearchResult[];
  scope: { channelId?: string; dmThreadId?: string };
}

export const FilesList: React.FC<FilesListProps> = ({ messages }) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('bg-BG', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return 'Unknown size';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getFileIcon = (mimeType?: string) => {
    if (!mimeType) return '📄';
    if (mimeType.startsWith('image/')) return '🖼️';
    if (mimeType.startsWith('video/')) return '🎥';
    if (mimeType.startsWith('audio/')) return '🎵';
    if (mimeType.includes('pdf')) return '📕';
    if (mimeType.includes('word') || mimeType.includes('document')) return '📘';
    if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return '📊';
    if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) return '📙';
    if (mimeType.includes('zip') || mimeType.includes('archive')) return '📦';
    return '📄';
  };

  // Extract all file attachments from messages
  const fileItems = messages.flatMap((message) =>
    (message.message_attachments || []).map((attachment) => ({
      ...attachment,
      message,
    }))
  );

  const handleDownload = (fileUrl: string, fileName: string) => {
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (fileItems.length === 0) {
    return null;
  }

  return (
    <div className="files-list">
      {fileItems.map((item, index) => (
        <div key={`${item.id}-${index}`} className="file-card">
          <div className="file-icon-container">
            <span className="file-icon">{getFileIcon(item.mime_type)}</span>
          </div>

          <div className="file-details">
            <h4 className="file-name">{item.file_name}</h4>
            <p className="file-info">
              {formatFileSize(item.file_size)} · {item.mime_type || 'Unknown type'}
            </p>
            <p className="file-meta">
              Shared by{' '}
              {item.message.users.display_name ||
                `${item.message.users.first_name} ${item.message.users.last_name}`}{' '}
              · {formatDate(item.message.created_at)}
            </p>
          </div>

          <div className="file-actions">
            <button
              className="file-button download-button"
              onClick={() =>
                handleDownload(
                  item.file_url || `/api/attachments/${item.id}`,
                  item.file_name
                )
              }
            >
              📥 Download
            </button>
          </div>
        </div>
      ))}

      <style>{`
        .files-list {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .file-card {
          display: flex;
          align-items: center;
          gap: 1rem;
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 0.5rem;
          padding: 1rem;
          transition: box-shadow 0.2s;
        }

        .file-card:hover {
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        }

        .file-icon-container {
          flex-shrink: 0;
          width: 3rem;
          height: 3rem;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #f3f4f6;
          border-radius: 0.5rem;
        }

        .file-icon {
          font-size: 1.75rem;
        }

        .file-details {
          flex: 1;
          min-width: 0;
        }

        .file-name {
          margin: 0;
          font-size: 0.9375rem;
          font-weight: 600;
          color: #1f2937;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .file-info {
          margin: 0.25rem 0;
          font-size: 0.8125rem;
          color: #6b7280;
        }

        .file-meta {
          margin: 0.25rem 0 0;
          font-size: 0.75rem;
          color: #9ca3af;
        }

        .file-actions {
          flex-shrink: 0;
          display: flex;
          gap: 0.5rem;
        }

        .file-button {
          padding: 0.5rem 1rem;
          border: 1px solid #e5e7eb;
          border-radius: 0.375rem;
          background: white;
          font-size: 0.875rem;
          cursor: pointer;
          transition: all 0.2s;
        }

        .download-button {
          color: #3b82f6;
          border-color: #3b82f6;
        }

        .download-button:hover {
          background: #3b82f6;
          color: white;
        }

        @media (max-width: 768px) {
          .file-card {
            flex-direction: column;
            align-items: flex-start;
          }

          .file-actions {
            width: 100%;
          }

          .file-button {
            flex: 1;
          }
        }
      `}</style>
    </div>
  );
};
